/**
 * Post Store - Reactive State Management
 *
 * Manages the current feed with page-based navigation:
 * - Posts displayed in pages (4 posts per page)
 * - Highlight tracks selection within current page
 * - Background prefetch when nearing end of loaded posts
 * - Comment tree with toggleable collapse/expand
 *
 * Cache: simple in-memory TTL cache. Posts are held for `cacheDurationMs`
 * (read from window.APP_CONFIG at startup). On refresh (double-tap) the
 * cache is bypassed and fresh posts are fetched.
 */

import { RedditClient, RedditRateLimitError } from '../../api/reddit-client';
import { CachedPost, FeedConfig, RedditComment } from '../../core/types';
import { clamp } from '../../shared/utils';

type PostStoreListener = () => void;

export interface PostStoreState {
	posts: CachedPost[];
	currentPage: number; // Current page index (0, 1, 2...)
	postsPerPage: number; // Fixed at 4
	highlightedIndex: number; // Within current page (0-3)
	loading: boolean;
	loadingMore: boolean;
	hasMore: boolean;
	error: string | null;
	// Comment state
	comments: RedditComment[];
	commentsPage: number;
	hasMoreComments: boolean;
	commentsLoading: boolean;
	expandedComments: Set<string>;
	retryInSeconds: number | null;
}

export class PostStore {
	private readonly state: PostStoreState = {
		posts: [],
		currentPage: 0,
		postsPerPage: 4,
		highlightedIndex: 0,
		loading: true,
		loadingMore: false,
		hasMore: true,
		error: null,
		comments: [],
		commentsPage: 0,
		hasMoreComments: false,
		commentsLoading: false,
		expandedComments: new Set(),
		retryInSeconds: null,
	};

	private readonly listeners: PostStoreListener[] = [];
	private readonly client: RedditClient;
	private currentFeed: FeedConfig | null = null;
	private afterCursor: string | null = null;
	private retryTimer: ReturnType<typeof setInterval> | null = null;

	// Simple in-memory cache — no persistence, no IndexedDB
	private cachedPosts: CachedPost[] | null = null;
	private cacheTimestamp = 0;
	private readonly cacheDurationMs: number;

	constructor(client: RedditClient, cacheDurationMs: number) {
		this.client = client;
		this.cacheDurationMs = cacheDurationMs;
	}

	// ========================================================================
	// Subscriptions
	// ========================================================================

	subscribe(listener: PostStoreListener): () => void {
		this.listeners.push(listener);
		return () => {
			const index = this.listeners.indexOf(listener);
			if (index > -1) this.listeners.splice(index, 1);
		};
	}

	private notify(): void {
		this.listeners.forEach((l) => l());
	}

	// ========================================================================
	// Getters
	// ========================================================================

	getState(): PostStoreState {
		return { ...this.state };
	}

	/**
	 * Get post at current highlight position
	 */
	getHighlightedPost(): CachedPost | null {
		const absoluteIndex = this.state.currentPage * this.state.postsPerPage + this.state.highlightedIndex;
		return this.state.posts[absoluteIndex] ?? null;
	}

	/**
	 * Get current page's posts
	 */
	getCurrentPagePosts(): CachedPost[] {
		const start = this.state.currentPage * this.state.postsPerPage;
		return this.state.posts.slice(start, start + this.state.postsPerPage);
	}

	/**
	 * Get total pages available
	 */
	getTotalPages(): number {
		return Math.ceil(this.state.posts.length / this.state.postsPerPage);
	}

	// ========================================================================
	// Feed Loading
	// ========================================================================

	async loadFeed(config: FeedConfig, forceRefresh = false): Promise<void> {
		this.state.loading = true;
		this.state.error = null;
		this.state.currentPage = 0;
		this.state.highlightedIndex = 0;
		this.state.hasMore = true;
		this.state.posts = [];
		this.afterCursor = null;
		this.currentFeed = config;
		this.notify();

		let keepRetryCountdown = false;

		try {
			// Return cached posts if still fresh and not forced
			if (!forceRefresh && this.cachedPosts !== null && Date.now() - this.cacheTimestamp < this.cacheDurationMs) {
				console.log(
					`[PostStore] Cache hit (${this.cachedPosts.length} posts, age=${Math.round((Date.now() - this.cacheTimestamp) / 1000)}s)`,
				);
				this.state.posts = this.cachedPosts;
				this.state.loading = false;
				this.notify();
				return;
			}

			const { posts: fresh, after } = await this.client.fetchFeed(config);
			this.afterCursor = after;
			this.state.hasMore = after !== null;

			const posts = fresh.map((p) => ({ ...p, cachedAt: Date.now(), seen: false }));
			this.state.posts = posts;
			this.cachedPosts = posts;
			this.cacheTimestamp = Date.now();
			console.log(`[PostStore] Fetched ${posts.length} posts, cached for ${Math.round(this.cacheDurationMs / 1000)}s`);
		} catch (err) {
			if (err instanceof RedditRateLimitError) {
				keepRetryCountdown = true;
				this.state.error = null;
				this.startRetryCountdown(err.retryAfterSeconds, () => this.loadFeed(config, forceRefresh));
				return;
			}
			const errorMsg = err instanceof Error ? err.message : 'Failed to load feed';
			this.state.error = errorMsg;
			console.error('[PostStore] loadFeed error:', err);
			// Log additional context for debugging
			if (err instanceof Error && err.cause) {
				console.error('[PostStore] Error cause:', err.cause);
			}
		} finally {
			this.state.loading = false;
			if (!keepRetryCountdown) this.clearRetryCountdown();
			this.notify();
		}
	}

	startRetryCountdown(seconds: number, onElapsed?: () => Promise<void> | void): void {
		this.clearRetryTimer();
		this.state.retryInSeconds = Math.max(1, Math.ceil(seconds));
		this.state.error = null;
		this.notify();

		this.retryTimer = setInterval(() => {
			if (this.state.retryInSeconds === null) {
				this.clearRetryTimer();
				return;
			}

			this.state.retryInSeconds--;

			if (this.state.retryInSeconds <= 0) {
				this.clearRetryTimer();
				this.state.retryInSeconds = null;
				this.notify();
				void onElapsed?.();
				return;
			}

			this.notify();
		}, 1000);
	}

	clearRetryCountdown(): void {
		this.clearRetryTimer();
		this.state.retryInSeconds = null;
		this.notify();
	}

	async refresh(): Promise<void> {
		if (this.currentFeed) {
			await this.loadFeed(this.currentFeed, true);
		}
	}

	/**
	 * Synchronously prepares the store for a new feed load.
	 * Clears existing posts and sets loading state instantly to prevent UI flashes.
	 */
	prepareForNewLoad(): void {
		this.state.loading = true;
		this.state.posts = [];
		this.state.currentPage = 0;
		this.state.highlightedIndex = 0;
		this.state.error = null;
		this.notify();
	}

	/**
	 * Load feed with a different endpoint (transient — does not persist to config).
	 * Always force-fetches fresh posts, bypassing the in-memory cache.
	 */
	async loadFeedByEndpoint(endpoint: import('../../core/types').FeedEndpoint): Promise<void> {
		// New endpoint selection from menu should start fresh (no old subreddit/sort/time)
		// We expect prepareForNewLoad to have been called by the caller if they want an instant reset.
		await this.loadFeed({ endpoint, limit: 25 }, true);
	}

	// ========================================================================
	// Page Navigation
	// ========================================================================

	/**
	 * Go to next page, prefetch if needed
	 */
	async nextPage(): Promise<void> {
		const nextPage = this.state.currentPage + 1;
		const postsNeeded = (nextPage + 1) * this.state.postsPerPage;

		// Check if we need more posts
		if (postsNeeded > this.state.posts.length && this.state.hasMore && !this.state.loadingMore) {
			await this.loadMore();
		}

		// Only advance if we have posts for that page
		if (nextPage * this.state.postsPerPage < this.state.posts.length) {
			this.state.loadingMore = true;
			this.notify();

			await new Promise((resolve) => setTimeout(resolve, 500));

			this.state.currentPage = nextPage;
			this.state.loadingMore = false;
			this.state.highlightedIndex = 0;
			this.notify();
		}
	}

	/**
	 * Go to previous page
	 */
	prevPage(): void {
		if (this.state.currentPage > 0) {
			this.state.currentPage--;
			this.state.highlightedIndex = 3;
			this.notify();
		}
	}

	/**
	 * Set highlight index within current page
	 */
	setHighlight(index: number): void {
		const pagePosts = this.getCurrentPagePosts();
		const lastPostIndex = Math.max(0, pagePosts.length - 1);
		const clamped = clamp(index, 0, lastPostIndex);
		if (clamped !== this.state.highlightedIndex) {
			this.state.highlightedIndex = clamped;
			this.notify();
		}
	}

	/**
	 * Append more posts from Reddit (pagination cursor, not cached)
	 */
	private async loadMore(): Promise<void> {
		if (!this.currentFeed || !this.afterCursor || this.state.loadingMore) {
			return;
		}

		this.state.loadingMore = true;
		this.notify();

		let keepRetryCountdown = false;

		try {
			const { posts: fresh, after } = await this.client.fetchFeed(this.currentFeed, this.afterCursor);
			this.afterCursor = after;
			this.state.hasMore = after !== null;

			const existingIds = new Set(this.state.posts.map((p) => p.id));
			const newPosts = fresh
				.filter((p) => !existingIds.has(p.id))
				.map((p) => ({ ...p, cachedAt: Date.now(), seen: false }));

			this.state.posts = [...this.state.posts, ...newPosts];
			console.log(`[PostStore] loadMore: added ${newPosts.length} posts, total=${this.state.posts.length}`);
		} catch (err) {
			if (err instanceof RedditRateLimitError) {
				keepRetryCountdown = true;
				this.startRetryCountdown(err.retryAfterSeconds, () => this.loadMore());
				return;
			}
			console.error('[PostStore] loadMore error:', err);
		} finally {
			this.state.loadingMore = false;
			if (!keepRetryCountdown && this.state.retryInSeconds !== null) this.clearRetryCountdown();
			this.notify();
		}
	}

	// ========================================================================
	// Comments
	// ========================================================================

	async loadComments(signal?: AbortSignal): Promise<void> {
		const post = this.getHighlightedPost();
		if (!post) return;

		this.state.commentsLoading = true;
		this.state.comments = [];
		this.state.commentsPage = 0;
		this.state.hasMoreComments = false;
		this.state.expandedComments.clear();
		this.notify();

		let keepRetryCountdown = false;

		try {
			const comments = await this.client.fetchComments(post.id);
			if (signal?.aborted) {
				console.log('[PostStore] loadComments aborted');
				return;
			}
			this.state.comments = comments;
			this.state.hasMoreComments = comments.length === 10;
		} catch (err) {
			if (err instanceof RedditRateLimitError) {
				keepRetryCountdown = true;
				this.startRetryCountdown(err.retryAfterSeconds, () => {
					if (!signal?.aborted) return this.loadComments(signal);
				});
				return;
			}
			if (signal?.aborted) {
				console.log('[PostStore] loadComments aborted (during error)');
				return;
			}
			console.error('[PostStore] loadComments error:', err);
			this.state.comments = [];
		} finally {
			this.state.commentsLoading = false;
			if (!keepRetryCountdown && this.state.retryInSeconds !== null) this.clearRetryCountdown();
			this.notify();
		}
	}

	async loadMoreComments(signal?: AbortSignal): Promise<void> {
		const post = this.getHighlightedPost();
		if (!post || !this.state.hasMoreComments || this.state.commentsLoading) return;

		this.state.commentsLoading = true;
		this.notify();

		let keepRetryCountdown = false;

		try {
			const moreComments = await this.client.fetchComments(post.id);
			if (signal?.aborted) {
				console.log('[PostStore] loadMoreComments aborted');
				return;
			}
			this.state.comments.push(...moreComments);
			this.state.commentsPage++;
			this.state.hasMoreComments = moreComments.length === 10;
		} catch (err) {
			if (err instanceof RedditRateLimitError) {
				keepRetryCountdown = true;
				this.startRetryCountdown(err.retryAfterSeconds, () => {
					if (!signal?.aborted) return this.loadMoreComments(signal);
				});
				return;
			}
			if (signal?.aborted) {
				console.log('[PostStore] loadMoreComments aborted (during error)');
				return;
			}
			console.error('[PostStore] loadMoreComments error:', err);
		} finally {
			this.state.commentsLoading = false;
			if (!keepRetryCountdown && this.state.retryInSeconds !== null) this.clearRetryCountdown();
			this.notify();
		}
	}

	private clearRetryTimer(): void {
		if (this.retryTimer) {
			clearInterval(this.retryTimer);
			this.retryTimer = null;
		}
	}
}
